/**
 * Hook PWA pour Squash Scoring App
 * 
 * Gère l'installation, les mises à jour et les fonctionnalités PWA
 * Compatible avec Create React App et TypeScript
 * 
 * @module hooks/usePWA
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';

// Types pour les événements PWA
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAState {
  // États d'installation
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  
  // États du service worker
  isServiceWorkerReady: boolean;
  isServiceWorkerUpdated: boolean;
  serviceWorkerRegistration: ServiceWorkerRegistration | null;
  
  // États de connexion
  isOnline: boolean;

  // ✅ NOUVEAUX : États iOS
  isIOS: boolean;
  showIOSInstallPrompt: boolean;
  
  // Méthodes
  promptInstall: () => Promise<void>;
  reloadApp: () => void;
  clearCache: () => Promise<void>;
  checkForUpdates: () => Promise<void>;

  // ✅ NOUVELLES : Méthodes iOS
  showIOSInstructions: () => void;
  hideIOSInstructions: () => void;
}

/**
 * Hook principal pour gérer les fonctionnalités PWA
 */
export const usePWA = (): PWAState => {
  // États locaux
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isServiceWorkerReady, setIsServiceWorkerReady] = useState(false);
  const [isServiceWorkerUpdated, setIsServiceWorkerUpdated] = useState(false);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  // ✅ NOUVEAU : Détection iOS
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstallPrompt, setShowIOSInstallPrompt] = useState(false);

  /**
   * Détecte si l'app est installée
   */
  const detectInstallation = useCallback(() => {
    // Méthode 1: Mode standalone
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    
    // Méthode 2: iOS Safari
    const isIOSStandalone = (window.navigator as any).standalone === true;
    
    // Méthode 3: Android Chrome
    const isAndroidStandalone = document.referrer.startsWith('android-app://');
    
    const standalone = isStandaloneMode || isIOSStandalone || isAndroidStandalone;
    
    setIsStandalone(standalone);
    setIsInstalled(standalone);
    
    console.log('[PWA] État d\'installation:', {
      standalone,
      isStandaloneMode,
      isIOSStandalone,
      isAndroidStandalone
    });
  }, []);

  /**
   * Détecte si l'utilisateur est sur iOS Safari
   */
  const detectIOSDevice = useCallback(() => {
    const userAgent = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isIOSSafari = isIOSDevice && isSafari;
    
    setIsIOS(isIOSSafari);
    
    // Sur iOS Safari, si pas installé, montrer le prompt après un délai
    if (isIOSSafari && !isStandalone) {
      setTimeout(() => {
        setShowIOSInstallPrompt(true);
      }, 3000);
    }
    
    console.log('[PWA] Détection iOS:', { isIOSDevice, isSafari, isIOSSafari });
  }, [isStandalone]);

  // ✅ NOUVEAU : Méthode pour iOS
  const showIOSInstructions = useCallback(() => {
    setShowIOSInstallPrompt(true);
  }, []);

  const hideIOSInstructions = useCallback(() => {
    setShowIOSInstallPrompt(false);
  }, []);

  /**
   * Enregistre le service worker
   */
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn('[PWA] Service Worker non supporté');
      return;
    }

    try {
      console.log('[PWA] Enregistrement du Service Worker...');
      
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      
      setServiceWorkerRegistration(registration);
      console.log('[PWA] Service Worker enregistré:', registration);

      // Vérifier si SW est prêt
      if (registration.active) {
        setIsServiceWorkerReady(true);
        console.log('[PWA] Service Worker actif');
      }

      // Écouter les changements d'état
      registration.addEventListener('updatefound', () => {
        console.log('[PWA] Mise à jour du Service Worker détectée');
        
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('[PWA] Nouvelle version disponible');
            setIsServiceWorkerUpdated(true);
          }
        });
      });

      // Écouter les messages du SW
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('[PWA] Message du Service Worker:', event.data);
      });

    } catch (error) {
      console.error('[PWA] Erreur enregistrement Service Worker:', error);
    }
  }, []);

  /**
   * Gère l'événement beforeinstallprompt
   */
  const handleBeforeInstallPrompt = useCallback((event: Event) => {
    console.log('[PWA] Événement beforeinstallprompt détecté');
    
    // Empêche l'affichage automatique
    event.preventDefault();
    
    // Stocke l'événement pour usage ultérieur
    setDeferredPrompt(event as BeforeInstallPromptEvent);
    setIsInstallable(true);
  }, []);

  /**
   * Gère l'événement appinstalled
   */
  const handleAppInstalled = useCallback(() => {
    console.log('[PWA] Application installée');
    setIsInstalled(true);
    setIsInstallable(false);
    setDeferredPrompt(null);
  }, []);

  /**
   * Gère les changements de connexion
   */
  const handleOnlineStatusChange = useCallback(() => {
    const online = navigator.onLine;
    setIsOnline(online);
    console.log('[PWA] État connexion:', online ? 'En ligne' : 'Hors ligne');
  }, []);

  /**
   * Invite l'utilisateur à installer l'app
   */
  const promptInstall = useCallback(async (): Promise<void> => {
    if (!deferredPrompt) {
      console.warn('[PWA] Pas de prompt d\'installation disponible');
      return;
    }

    try {
      // Affiche le prompt d'installation
      await deferredPrompt.prompt();
      
      // Attend la réponse de l'utilisateur
      const choiceResult = await deferredPrompt.userChoice;
      
      console.log('[PWA] Choix utilisateur:', choiceResult.outcome);
      
      if (choiceResult.outcome === 'accepted') {
        console.log('[PWA] Installation acceptée');
      } else {
        console.log('[PWA] Installation refusée');
      }
      
      // Nettoie le prompt
      setDeferredPrompt(null);
      setIsInstallable(false);
      
    } catch (error) {
      console.error('[PWA] Erreur prompt installation:', error);
    }
  }, [deferredPrompt]);

  /**
   * Recharge l'application (pour les mises à jour)
   */
  const reloadApp = useCallback((): void => {
    console.log('[PWA] Rechargement de l\'application');
    window.location.reload();
  }, []);

  /**
   * Vide le cache
   */
  const clearCache = useCallback(async (): Promise<void> => {
    try {
      if (serviceWorkerRegistration?.active) {
        // Envoie un message au SW pour vider le cache
        const messageChannel = new MessageChannel();
        serviceWorkerRegistration.active.postMessage(
          { type: 'CLEAR_CACHE' },
          [messageChannel.port2]
        );
        console.log('[PWA] Demande de vidage du cache envoyée');
      }
      
      // Vide aussi le cache du navigateur si possible
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => caches.delete(name))
        );
        console.log('[PWA] Cache navigateur vidé');
      }
      
    } catch (error) {
      console.error('[PWA] Erreur vidage cache:', error);
    }
  }, [serviceWorkerRegistration]);

  /**
   * Vérifie les mises à jour manuellement
   */
  const checkForUpdates = useCallback(async (): Promise<void> => {
    if (!serviceWorkerRegistration) {
      console.warn('[PWA] Aucun Service Worker enregistré');
      return;
    }

    try {
      console.log('[PWA] Vérification des mises à jour...');
      await serviceWorkerRegistration.update();
      console.log('[PWA] Vérification des mises à jour terminée');
    } catch (error) {
      console.error('[PWA] Erreur vérification mises à jour:', error);
    }
  }, [serviceWorkerRegistration]);

  // Effects
  useEffect(() => {
    // Détection initiale
    detectInstallation();
    
    // Enregistrement du service worker
    registerServiceWorker();
    
    // Event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
    
    // Nettoyage
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, [
    detectInstallation,
    registerServiceWorker,
    handleBeforeInstallPrompt,
    handleAppInstalled,
    handleOnlineStatusChange
  ]);

  useEffect(() => {
    // Vérification périodique des mises à jour (toutes les 30 minutes)
    const interval = setInterval(() => {
      // Ne pas vérifier si l'onglet est caché (économie de ressources)
      if (!document.hidden && serviceWorkerRegistration) {
        console.log('[PWA] Vérification automatique des mises à jour...');
        checkForUpdates();
      }
    }, 30 * 60 * 1000); // 30 minutes en millisecondes

    // Vérification initiale après 1 minute (pour laisser l'app se charger)
    const initialCheck = setTimeout(() => {
      if (serviceWorkerRegistration) {
        console.log('[PWA] Première vérification des mises à jour...');
        checkForUpdates();
      }
    }, 60 * 1000); // 1 minute

    // Nettoyage des timers
    return () => {
      clearInterval(interval);
      clearTimeout(initialCheck);
    };
  }, [checkForUpdates, serviceWorkerRegistration]); // Dépendances importantes
  
  useEffect(() => {
    detectIOSDevice();
  }, [detectIOSDevice]);

  // Retour de l'état et des méthodes
  return {
    // États
    isInstallable,
    isInstalled,
    isStandalone,
    isServiceWorkerReady,
    isServiceWorkerUpdated,
    serviceWorkerRegistration,
    isOnline,
    
    // ✅ NOUVEAUX états iOS
    isIOS,
    showIOSInstallPrompt,
    
    // Méthodes
    promptInstall,
    reloadApp,
    clearCache,
    checkForUpdates,

    // ✅ NOUVELLES méthodes iOS
    showIOSInstructions,
    hideIOSInstructions
  };
};

/**
 * Hook simplifié pour vérifier si l'app est installée
 */
export const useIsInstalled = (): boolean => {
  const { isInstalled } = usePWA();
  return isInstalled;
};

/**
 * Hook pour les notifications hors-ligne
 */
export const useOfflineNotification = () => {
  const { isOnline } = usePWA();
  
  useEffect(() => {
    if (!isOnline) {
      // Ici tu peux ajouter une notification ou un toast
      console.log('[PWA] Mode hors-ligne activé');
    }
  }, [isOnline]);
  
  return { isOnline };
};